import { Warehouse, Calendar, FileText, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Card, Col, Container, Row, Table } from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import BreadCrumb from '../../components/Label/Breadcrumb'
import Loading from '../../components/atoms/Loading'
import api from '../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../helpers/constants'
import { showPopUpErrorMessage } from '../../utils/Common'
import toast, { Toaster } from 'react-hot-toast'

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#999',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const valueDetailStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#333333',
  marginBottom: '0'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const DetailMaterialRequest = () => {
  const { id } = useParams()
  const { state } = useLocation()

  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!', {
      position: 'top-right',
      style: {
        fontSize: '14px',
        fontWeight: '600'
      }
    })
  }

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setIsLoading(true)
        const response = await api.post(`/material-request/detail`, {
          mr_code: id
        })
        const { data } = response
        if (data.success) {
          setDetail(data.data)
        }
      } catch (error) {
        const errMsg =
          error?.response?.data?.errors?.message || 'An error occurred'
        showPopUpErrorMessage(errMsg)
      } finally {
        setIsLoading(false)
      }
    }

    fetchDetail()
  }, [id])

  if (isLoading) return <Loading />
  if (!detail) return null

  const { target_name, delivery_date, code, items = [] } = detail

  return (
    <Container fluid className="px-4 mb-5">
      <Row>
        <Col md={12} className="mt-3 mb-4">
          <BreadCrumb
            label={code}
            link="/material-request/"
            back="Material Request"
            stateValue={state}
          />
        </Col>
      </Row>

      <Card style={cardStyle}>
        <Row className="gy-4">
          <Col md={6} lg={4}>
            <div className="d-flex align-items-center">
              <div
                className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                style={{ width: '48px', height: '48px' }}
              >
                <Warehouse className="text-primary" size={24} />
              </div>
              <div>
                <div style={labelDetailStyle}>TARGET WAREHOUSE</div>
                <div style={valueDetailStyle}>{target_name || '-'}</div>
              </div>
            </div>
          </Col>
          <Col md={6} lg={4}>
            <div className="d-flex align-items-center">
              <div
                className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                style={{ width: '48px', height: '48px' }}
              >
                <Calendar className="text-warning" size={24} />
              </div>
              <div>
                <div style={labelDetailStyle}>DELIVERY DATE</div>
                <div style={valueDetailStyle}>
                  {delivery_date ? convertDateWithoutTime(delivery_date) : '-'}
                </div>
              </div>
            </div>
          </Col>
          <Col md={6} lg={4}>
            <div className="d-flex align-items-center">
              <div
                className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                style={{ width: '48px', height: '48px' }}
              >
                <FileText className="text-success" size={24} />
              </div>
              <div>
                <div style={labelDetailStyle}>MR CODE</div>
                <div
                  style={valueDetailStyle}
                  className="d-flex align-items-center gap-2"
                >
                  {code || '-'}
                  {code && (
                    <Copy
                      size={14}
                      className="text-muted cursor-pointer"
                      style={{
                        cursor: 'pointer',
                        opacity: 0.5,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = 0.5)
                      }
                      onClick={() => handleCopy(code)}
                      title="Copy MR Code"
                    />
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <h5
            className="mb-3 text-secondary fw-bold"
            style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
          >
            Request Items ({items.length})
          </h5>
          <Table
            hover
            responsive
            className="shadow-sm bg-white"
            style={{
              fontSize: '0.9rem',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th
                  className="p-2 align-middle"
                  style={{ ...tableHeaderStyle, width: '50px' }}
                >
                  #
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Item Name
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Item Alias
                </th>
                <th className="p-2 align-middle" style={tableHeaderStyle}>
                  Item Code
                </th>
                <th
                  className="p-2 text-end align-middle"
                  style={tableHeaderStyle}
                >
                  Qty
                </th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center p-4 text-muted fw-bold"
                  >
                    No Data
                  </td>
                </tr>
              ) : (
                items.map((item, index) => (
                  <tr
                    key={item.id || index}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    <td className="p-2 align-middle text-center text-muted">
                      {index + 1}
                    </td>
                    <td className="p-2 align-middle fw-semibold text-primary">
                      {item.item_name}
                    </td>
                    <td className="p-2 align-middle">
                      {item.item_alias || '-'}
                    </td>
                    <td className="p-2 align-middle">{item.item_code}</td>
                    <td className="p-2 text-end align-middle fw-bold text-dark">
                      {item.total_qty} {item.uom_qty}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Col>
      </Row>
      <Toaster />
    </Container>
  )
}

export default DetailMaterialRequest
